import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import './store.css';
import { LoadingImage } from './styles';
import logo from '../../assets/logos/bolt.png';
import { cart, formatCents, shopApi, shopErrorMessage } from '../../features/marketplace';
import type { OrderStatus } from '../../features/marketplace';
import { lastOrder, rememberOrder } from '../../features/marketplace/checkout';

// Where a card payment comes back to. 450labs adds hl_return; it is traded once for
// the order id and token, then the payment is confirmed. If the nonce was already
// spent (a reload), the order kept from the checkout answer is used instead.

type State =
  | { kind: 'loading' }
  | { kind: 'done'; order: OrderStatus; paid: boolean }
  | { kind: 'error'; message: string };

const OrderPage: React.FC = () => {
  const [params] = useSearchParams();
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    document.title = 'NΛSOS Store';
    const nonce = params.get('hl_return');
    let cancelled = false;
    (async () => {
      try {
        let ids = null;
        if (nonce) {
          ids = await shopApi.tradeReturn(nonce).catch(() => null);
          if (ids) rememberOrder(ids);
        }
        if (!ids) ids = lastOrder();
        if (!ids) throw new Error('no_order');
        const verified = await shopApi.verifyPayment(ids.order_id, ids.order_token);
        const order = await shopApi.getOrder(ids.order_id, ids.order_token);
        if (verified.paid) cart.clear();
        if (!cancelled) setState({ kind: 'done', order, paid: verified.paid });
      } catch (err) {
        if (!cancelled) {
          setState({
            kind: 'error',
            message: err instanceof Error && err.message === 'no_order' ? "We couldn't find that order." : shopErrorMessage(err),
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [params]);

  return (
    <div className="store-order-page">
      <div className="store store-message">
        {state.kind === 'loading' && (
          <>
            <LoadingImage src={logo} alt="Loading..." />
            <p className="light-text">checking the payment...</p>
          </>
        )}
        {state.kind === 'done' && (
          <>
            <div className="store-title">{state.paid ? 'paid, thank you!' : 'not paid yet'}</div>
            <p className="light-text">
              order <b>{state.order.order_id.slice(0, 8)}</b> · <b>{formatCents(state.order.total_cents, state.order.currency)}</b>
            </p>
            {!state.paid && (
              <p className="light-text">If you closed the payment page, your cart is still in the store.</p>
            )}
            {state.order.is_test && <p className="store-notice">This was a test order: the store is still in test mode.</p>}
          </>
        )}
        {state.kind === 'error' && (
          <>
            <div className="store-title">Something's off</div>
            <p className="light-text">{state.message}</p>
          </>
        )}
        <Link to="/marketplace" className="selection-button store-back">
          back to the store
        </Link>
      </div>
    </div>
  );
};

export default OrderPage;
